import {
  CacheStore,
  CacheConfig,
  CacheData,
  StoreEvents
} from './cache/interfaces';
import getSizeBytes from './cache/getSizeBytes';
import logger from '../../log';

const log = logger.getLogger(__filename);

function getFirst<T>(cacheMap: Map<string, T>) {
  return cacheMap.keys().next().value;
}

interface CacheValue {
  value: CacheData;
  timestamp: number;
}

export default class LRUBytesCache implements CacheStore<CacheData> {
  config: CacheConfig;
  events: StoreEvents;
  cache: Map<string, CacheValue>;
  cachedBytes: number;
  maxBytes: number;

  /**
   * Create an instance of LRUBytesCache.
   * @param config - cache configuration
   */
  constructor(config: CacheConfig, events: StoreEvents) {
    this.config = config;
    this.events = events;
    this.cache = new Map();
    this.cachedBytes = 0;
    this.maxBytes = config.maxBytes || 256000000;
  }

  initialize(): Promise<void> {
    return Promise.resolve();
  }

  _getWithRetry(key: string): Promise<CacheValue | undefined> {
    const cacheEntry = this.cache.get(key);
    // SRV-2566: small segments not peering.
    // There can be data races with WASM boundries. This gives
    // the cache the opportunity to retry fetch if it "has" the
    // object but the value isn't available because of volatility
    // in memory.
    if (!cacheEntry || !cacheEntry.value) {
      return new Promise(res => {
        setTimeout(() => {
          res(this.cache.get(key) || undefined);
        }, 0);
      });
    }
    return Promise.resolve(cacheEntry);
  }

  async get(key: string): Promise<CacheData | undefined> {
    const obj = await this._getWithRetry(key);
    if (obj) {
      // remove and re add at the end.
      this.cache.delete(key);
      this.cache.set(key, obj);
      return Promise.resolve(obj.value);
    }
    return Promise.resolve(undefined);
  }

  set(key: string, value: CacheData): Promise<boolean> {
    const valSize = getSizeBytes(value);
    if (valSize > this.maxBytes) {
      log.error(`payload for ${key} is too large at ${valSize} bytes.`);
      return Promise.resolve(false);
    }
    if (this.cache.has(key)) {
      this._innerDelete(key);
    }

    while (this.cachedBytes + valSize > this.maxBytes) {
      const toRemove = getFirst(this.cache);
      // if there is no first item, we should NOT be looping.
      if (!toRemove) {
        // if we are here, we should have an empty map. cached Bytes should be reset
        if (this.cache.size === 0) {
          this.cachedBytes = 0;
        }
        break;
      }
      this.delete(toRemove);
    }
    this.cachedBytes = this.cachedBytes + valSize;
    this.cache.set(key, { value, timestamp: Date.now() });
    this.events.onset({ key });
    return Promise.resolve(true);
  }

  has(key: string): boolean {
    return this.cache.has(key);
  }

  keys(): IterableIterator<string> {
    return this.cache.keys();
  }

  _innerDelete(key: string): boolean {
    const item = this.cache.get(key);
    if (item) {
      this.cachedBytes = this.cachedBytes - getSizeBytes(item.value);
    }
    return this.cache.delete(key);
  }

  delete(key: string): Promise<boolean> {
    const result = this._innerDelete(key);

    return new Promise(res => {
      this.events.ondelete({ key });
      res(result);
    });
  }

  getPayloadSize(key: string): Promise<number> {
    const item = this.cache.get(key);
    return Promise.resolve(item ? getSizeBytes(item.value) : 0);
  }

  getTimestamp(key: string): Promise<number> {
    const item = this.cache.get(key);
    return Promise.resolve(item ? item.timestamp : 0);
  }

  clear(): void {
    this.cachedBytes = 0;
    this.cache.clear();
  }
}
